// The Academy catalog: every course and every lesson, plus the lookups the
// section's pages share. Lessons are auto-discovered from ./lessons/** with
// the same require.context idiom as tutorials/index.ts and scenes/index.ts;
// to add one, drop a file under ./lessons/<courseId>/ that default-exports a
// LessonDefinition. Nothing here needs editing.
import { COURSES } from "./courses";
import { Course, LessonDefinition } from "./types";

export { COURSES };

export interface CourseProgressSummary {
  done: number;
  total: number;
  /** 0–100, rounded. */
  pct: number;
}

/** The slice of AcademyProgress the catalog reads (kept structural so this
 *  module never imports React or axios). */
export interface CompletedLookup {
  completed?: Record<string, string>;
}

const isLesson = (m: unknown): m is LessonDefinition =>
  !!m && typeof m === "object" && typeof (m as LessonDefinition).id === "string" && Array.isArray((m as LessonDefinition).body);

function loadLessonsFromContext(): LessonDefinition[] {
  try {
    // Webpack replaces this call at build time. Jest has no require.context,
    // so the call throws there and catalog.test.ts loads the folder with fs.
    const ctx = (require as any).context("./lessons", true, /\.ts$/);
    return ctx
      .keys()
      .filter((key: string) => !/\.(test|d)\.ts$/.test(key))
      .map((key: string) => {
        const mod = ctx(key);
        return mod && mod.default;
      })
      .filter(isLesson);
  } catch (err) {
    if (process.env.NODE_ENV !== "test") throw err;
    return [];
  }
}

/** Course order first, then lesson order, then id so the sort is total. */
export function sortLessons(lessons: LessonDefinition[], courses: Course[]): LessonDefinition[] {
  const courseOrder = new Map(courses.map((c) => [c.id, c.order]));
  const orderOf = (l: LessonDefinition) => courseOrder.get(l.courseId) ?? Number.MAX_SAFE_INTEGER;
  return [...lessons].sort((a, b) => orderOf(a) - orderOf(b) || a.order - b.order || a.id.localeCompare(b.id));
}

/**
 * Builds the lookups over a given set of lessons and courses. The module
 * exports below are this applied to the auto-discovered set; tests call it
 * with the lessons they loaded themselves.
 */
export function buildCatalog(lessons: LessonDefinition[], courses: Course[]) {
  const all = sortLessons(lessons, courses);
  const sortedCourses = [...courses].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  const byId = new Map(all.map((l) => [l.id, l]));
  const byCourse = new Map<string, LessonDefinition[]>();
  for (const l of all) {
    const list = byCourse.get(l.courseId);
    if (list) list.push(l);
    else byCourse.set(l.courseId, [l]);
  }

  const getCourse = (id: string): Course | undefined => sortedCourses.find((c) => c.id === id);
  const getLesson = (id: string): LessonDefinition | undefined => byId.get(id);
  const lessonsForCourse = (courseId: string): LessonDefinition[] => byCourse.get(courseId) ?? [];
  const courseMinutes = (courseId: string): number => lessonsForCourse(courseId).reduce((n, l) => n + (l.minutes || 0), 0);

  /** Previous and next lesson within the same course (undefined at the ends). */
  const adjacentLessons = (lessonId: string): { prev?: LessonDefinition; next?: LessonDefinition } => {
    const lesson = byId.get(lessonId);
    if (!lesson) return {};
    const list = lessonsForCourse(lesson.courseId);
    const i = list.findIndex((l) => l.id === lessonId);
    return { prev: i > 0 ? list[i - 1] : undefined, next: i >= 0 && i < list.length - 1 ? list[i + 1] : undefined };
  };

  /** Lessons released strictly after `isoDate`, newest first. An unparseable
   *  or missing date returns nothing: a brand-new account is not "behind". */
  const newLessonsSince = (isoDate: string | Date | null | undefined): LessonDefinition[] => {
    const since = isoDate ? new Date(isoDate).getTime() : NaN;
    if (!Number.isFinite(since)) return [];
    return all
      .filter((l) => Date.parse(l.releasedAt) > since)
      .sort((a, b) => Date.parse(b.releasedAt) - Date.parse(a.releasedAt) || a.id.localeCompare(b.id));
  };

  const courseProgress = (courseId: string, progress: CompletedLookup | null | undefined): CourseProgressSummary => {
    const list = lessonsForCourse(courseId);
    const completed = progress?.completed ?? {};
    const done = list.filter((l) => Boolean(completed[l.id])).length;
    const total = list.length;
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  };

  return {
    ALL_LESSONS: all,
    COURSES: sortedCourses,
    getCourse,
    getLesson,
    lessonsForCourse,
    courseMinutes,
    adjacentLessons,
    newLessonsSince,
    courseProgress,
  };
}

const catalog = buildCatalog(loadLessonsFromContext(), COURSES);

export const ALL_LESSONS: LessonDefinition[] = catalog.ALL_LESSONS;
export const getCourse = catalog.getCourse;
export const getLesson = catalog.getLesson;
export const lessonsForCourse = catalog.lessonsForCourse;
export const courseMinutes = catalog.courseMinutes;
export const adjacentLessons = catalog.adjacentLessons;
export const newLessonsSince = catalog.newLessonsSince;
export const courseProgress = catalog.courseProgress;
